import * as React from "react";
import arePropsEqual from "react-fast-compare";
import type {Descendant} from "slate";
import {Slate} from "slate-react";

import {useReferencesInitializer} from "@khan/guide-core";
import type {ChatInputMetadata} from "@khan/guide-types";
import {ReferencesContext} from "@khan/slate-contexts";
import {
    createSlateInput,
    onSlateChange,
    parseMessage,
    useSlateValue,
} from "@khan/slate-helpers";

import {ChatInput, type ChatInputComponent} from "./chat-input.tsx";

interface Props {
    disableSend: boolean;
    expanded?: boolean;
    onMessageSend: (message: string, metadata: ChatInputMetadata) => unknown;
    ChatInputComponent?: ChatInputComponent;
}

// NOTE(kevinb): This component adds input handling to the ChatInput component.
// This should probably be merged into that component, but it's already quite
// large and should be split up first.
const ChatInputUI = (props: Props) => {
    const editor = React.useMemo(() => createSlateInput(), []);
    const [value, setValue] = useSlateValue();
    const message = React.useRef(parseMessage(value));
    const references = useReferencesInitializer();
    const typingStartTimeDateRef = React.useRef<Date | null | undefined>(null);

    const {
        disableSend,
        expanded,
        onMessageSend,
        ChatInputComponent = ChatInput,
    } = props;

    const handleChange = React.useCallback(
        (value: Descendant[]) => {
            onSlateChange(editor, references);
            return setValue(() => {
                const updatedMessage = parseMessage(value);

                const userIsTypingNewMessage =
                    !message.current &&
                    message.current !== updatedMessage &&
                    !typingStartTimeDateRef.current;

                if (userIsTypingNewMessage) {
                    typingStartTimeDateRef.current = new Date();
                }

                message.current = updatedMessage;
                return value;
            });
        },
        [editor, references, setValue],
    );

    const handleMessageSend = React.useCallback(
        (metadata: ChatInputMetadata) => {
            // TODO(GL-1309): Move this computation into the chat-input.tsx
            // file, where we deal with all the other metadata.
            const typingStartDate = typingStartTimeDateRef.current;
            metadata.timeToSubmit = typingStartDate
                ? Date.now() - typingStartDate.getTime()
                : 0;
            typingStartTimeDateRef.current = null;

            onMessageSend(message.current, metadata);
        },
        [onMessageSend],
    );

    return (
        <Slate editor={editor} initialValue={value} onChange={handleChange}>
            <ReferencesContext.Provider value={references}>
                <ChatInputComponent
                    onSend={handleMessageSend}
                    message={message.current}
                    disableSend={disableSend}
                    expanded={expanded}
                />
            </ReferencesContext.Provider>
        </Slate>
    );
};

const MemoizedChatInputUI = React.memo(ChatInputUI, arePropsEqual);

export {MemoizedChatInputUI as ChatInputUI};
